import {
  FETCH_WITHDRAWS,
  FETCH_WITHDRAWS_SUCCESS,
  FETCH_WITHDRAWS_FAILED,
  EDIT_WITHDRAWS
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { child, update, get, push, remove } from "firebase/database";
import store from '../store/store';
import { RequestPushMsg } from '../other/NotificationFunctions';

// settings.withdrawFetchBoundDays (portal-only; absent/0 = legacy full read).
// Mirrors the useCustomerOnDemand flag read in usersactions.js.
const withdrawFetchBoundDays = () => {
  try {
    const days = Number(store.getState().settingsdata.settings.withdrawFetchBoundDays);
    return days > 0 ? days : 0;
  } catch (_) { return 0; }
};

export const fetchWithdraws = () => async (dispatch) => {

  const {
    withdrawRef,
    withdrawsUnprocessedRef,
    withdrawsSinceRef
  } = firebase;

  dispatch({
    type: FETCH_WITHDRAWS,
    payload: null
  });

  const boundDays = withdrawFetchBoundDays();
  if (boundDays > 0) {
    // Bounded read: every still-pending row (processed === false — cannot
    // lose a pending request, whatever its age) plus everything requested in
    // the last N days, merged by key (recent wins), newest first. Same
    // SUCCESS/FAILED types and the same `id = key` injection as legacy —
    // Withdraws.js actions and completeWithdraw depend on entry.id.
    try {
      const [pendingSnap, recentSnap] = await Promise.all([
        get(withdrawsUnprocessedRef),
        get(withdrawsSinceRef(Date.now() - boundDays * 86400000))
      ]);
      const merged = new Map();
      [pendingSnap.val() || {}, recentSnap.val() || {}].forEach(data => {
        Object.keys(data).forEach(i => {
          data[i].id = i
          merged.set(i, data[i]);
        });
      });
      const arr = Array.from(merged.values()).sort((a, b) => (b.date || 0) - (a.date || 0));
      if (arr.length) {
        dispatch({
          type: FETCH_WITHDRAWS_SUCCESS,
          payload: arr
        });
      } else {
        dispatch({
          type: FETCH_WITHDRAWS_FAILED,
          payload: "No WITHDRAWS available."
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_WITHDRAWS_FAILED,
        payload: error.message
      });
    }
    return;
  }

  try {
    const snapshot = await get(withdrawRef);
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: FETCH_WITHDRAWS_SUCCESS,
        payload: arr.reverse()
      });
    } else {
      dispatch({
        type: FETCH_WITHDRAWS_FAILED,
        payload: "No WITHDRAWS available."
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_WITHDRAWS_FAILED,
      payload: error.message
    });
  }
};

export const completeWithdraw = (entry) => async (dispatch) => {

  const {
    withdrawRef,
    walletHistoryRef
  } = firebase;
  
  // Include all fields including referenceCode, processDate, and status from the entry
  const updateData = {
    ...entry,
    processed: true,
    processDate: entry.processDate || new Date().getTime(),
    referenceCode: entry.referenceCode || '',
    status: entry.status || 'completed'
  };

  dispatch({
    type: EDIT_WITHDRAWS,
    payload: updateData
  });

  // Update the withdrawal record
  await update(child(withdrawRef, entry.id), updateData);
  
  // Update wallet history status from 'Processing' to 'Withdraw'
  if (entry.uid) {
    try {
      const walletHistorySnapshot = await get(walletHistoryRef(entry.uid));
      if (walletHistorySnapshot.exists()) {
        const walletHistory = walletHistorySnapshot.val();
        
        // Find the transaction with matching transaction_id and type 'Processing'
        for (const [key, transaction] of Object.entries(walletHistory)) {
          if (transaction.transaction_id === entry.id && transaction.type === 'Processing') {
            // Update the transaction type to 'Withdraw'
            await update(child(walletHistoryRef(entry.uid), key), {
              ...transaction,
              type: 'Withdraw',
              status: 'completed',
              processDate: entry.processDate || new Date().getTime(),
              referenceCode: entry.referenceCode || '',
              paymentSource: entry.paymentSource || '',
              processedBy: entry.processedBy || ''
            });
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error updating wallet history:', error);
    }
  }
}

export const transferWithdrawToTopUp = (entry, transferredBy, transferNote) => async (dispatch) => {

  const {
    withdrawRef,
    walletHistoryRef,
    singleUserRef
  } = firebase;

  const transferDate = new Date().getTime();

  const updateData = {
    processed: true,
    processDate: transferDate,
    processedBy: transferredBy,
    status: 'completed',
    paymentSource: 'Top-up Wallet',
    referenceCode: 'TOPUP-' + entry.id,
    transferredToTopUp: true,
    transferNote: transferNote || ''
  };

  dispatch({
    type: EDIT_WITHDRAWS,
    payload: { ...entry, ...updateData }
  });

  // Mark the withdrawal as processed via top-up transfer (only for pending withdrawals)
  await update(child(withdrawRef, entry.id), updateData);

  // Credit the driver's top-up wallet with the requested amount
  if (entry.uid && entry.amount) {
    try {
      const userSnapshot = await get(singleUserRef(entry.uid));
      if (!userSnapshot.exists()) {
        throw new Error('Driver not found');
      }
      const userData = userSnapshot.val();
      const settings = store.getState()?.settingsdata?.settings || {};
      const decimal = settings.decimal || 2;
      const currentTopUp = parseFloat(userData.topUpBalance) || 0;
      const newTopUp = parseFloat((currentTopUp + parseFloat(entry.amount)).toFixed(decimal));

      await update(singleUserRef(entry.uid), {
        topUpBalance: newTopUp
      });

      // Close out the 'Processing' entry as a completed withdrawal from the earning wallet
      const walletHistorySnapshot = await get(walletHistoryRef(entry.uid));
      if (walletHistorySnapshot.exists()) {
        const walletHistory = walletHistorySnapshot.val();
        for (const [key, transaction] of Object.entries(walletHistory)) {
          if (transaction.transaction_id === entry.id && transaction.type === 'Processing') {
            await update(child(walletHistoryRef(entry.uid), key), {
              ...transaction,
              type: 'Withdraw',
              status: 'completed',
              processDate: transferDate,
              paymentSource: 'Top-up Wallet',
              processedBy: transferredBy,
              note: 'Withdrawal amount transferred to top-up wallet'
            });
            break;
          }
        }
      }

      // Record the top-up credit in wallet history
      const creditEntry = {
        type: 'Credit',
        amount: parseFloat(entry.amount),
        date: transferDate,
        txRef: 'withdraw_topup_transfer_' + entry.id,
        transaction_id: 'topup_transfer_' + entry.id,
        description: 'Withdrawal request transferred to top-up wallet',
        note: `Amount from withdrawal request #${entry.id} moved to top-up wallet.${transferNote ? ' Note: ' + transferNote : ''} Transferred by: ${transferredBy}`
      };

      await push(walletHistoryRef(entry.uid), creditEntry);

      if (userData.pushToken) {
        const language = store.getState()?.languagedata?.defaultLanguage || {};
        RequestPushMsg(
          userData.pushToken,
          {
            title: language.notification_title || 'Wallet Updated',
            msg: (language.withdraw_topup_transfer_notification || 'Your withdrawal amount has been moved to your top-up wallet.') + (transferNote ? ' ' + transferNote : ''),
            screen: 'Wallet',
            ios: userData.userPlatform === "IOS"
          }
        );
      }
    } catch (error) {
      console.error('Error transferring withdrawal to top-up wallet:', error);
      throw error;
    }
  }
}

export const rejectWithdraw = (entry, rejectReason, rejectedBy) => async (dispatch) => {

  const {
    withdrawRef,
    walletHistoryRef,
    singleUserRef
  } = firebase;

  const rejectDate = new Date().getTime();

  const updateData = {
    processed: false,
    rejected: true,
    rejectDate: rejectDate,
    rejectReason: rejectReason || 'Withdrawal request rejected',
    rejectedBy: rejectedBy,
    status: 'rejected'
  };

  dispatch({
    type: EDIT_WITHDRAWS,
    payload: { ...entry, ...updateData }
  });

  // Mark the withdrawal as rejected/invalid. Unlike reverseWithdraw the
  // amount is NOT restored to the driver's earning wallet.
  await update(child(withdrawRef, entry.id), updateData);

  if (entry.uid) {
    try {
      // Close out the 'Processing' entry as a Debit so the driver's wallet
      // history reflects that the amount left the earning wallet for good.
      const walletHistorySnapshot = await get(walletHistoryRef(entry.uid));
      if (walletHistorySnapshot.exists()) {
        const walletHistory = walletHistorySnapshot.val();
        for (const [key, transaction] of Object.entries(walletHistory)) {
          if (transaction.transaction_id === entry.id && transaction.type === 'Processing') {
            await update(child(walletHistoryRef(entry.uid), key), {
              ...transaction,
              type: 'Debit',
              status: 'rejected',
              processDate: rejectDate,
              processedBy: rejectedBy,
              note: `Withdrawal request rejected. Reason: ${rejectReason || 'Invalid request'}`
            });
            break;
          }
        }
      }

      // Notify the driver with the rejection reason
      const userSnapshot = await get(singleUserRef(entry.uid));
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        if (userData.pushToken) {
          const language = store.getState()?.languagedata?.defaultLanguage || {};
          RequestPushMsg(
            userData.pushToken,
            {
              title: language.withdraw_rejected_title || 'Withdrawal Rejected',
              msg: (language.withdraw_rejected_notification || 'Your withdrawal request was rejected. Reason: ') + (rejectReason || ''),
              screen: 'Wallet',
              ios: userData.userPlatform === "IOS"
            }
          );
        }
      }
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      throw error;
    }
  }
}

export const reverseWithdraw = (entry, reverseReason, reversedBy) => async (dispatch) => {
  
  const {
    withdrawRef,
    walletHistoryRef,
    singleUserRef
  } = firebase;
  
  // Update the withdrawal record to mark it as cancelled (only for pending withdrawals)
  const updateData = {
    ...entry,
    processed: false,
    reversed: true,
    reverseDate: new Date().getTime(),
    reverseReason: reverseReason || 'Withdrawal request cancelled',
    reversedBy: reversedBy,
    status: 'cancelled'
  };

  dispatch({
    type: EDIT_WITHDRAWS,
    payload: updateData
  });

  // Update the withdrawal record
  await update(child(withdrawRef, entry.id), updateData);
  
  // Restore the wallet balance
  if (entry.uid && entry.amount) {
    try {
      // Get current user data
      let driverData = null;
      const userSnapshot = await get(singleUserRef(entry.uid));
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        driverData = userData;
        // Numbers, not text: a legacy string balance "100" plus 50 used to
        // be written back as "10050" (and the money-lock rules refuse a
        // non-number balance). Rounded like transferWithdrawToTopUp.
        const settings = store.getState()?.settingsdata?.settings || {};
        const decimal = settings.decimal || 2;
        const currentBalance = parseFloat(userData.walletBalance) || 0;
        const restoredBalance = parseFloat((currentBalance + (parseFloat(entry.amount) || 0)).toFixed(decimal));

        // Update user's wallet balance
        await update(singleUserRef(entry.uid), {
          walletBalance: restoredBalance
        });
      }

      // Handle wallet history - delete Processing entry and add Credit entry
      const walletHistorySnapshot = await get(walletHistoryRef(entry.uid));
      if (walletHistorySnapshot.exists()) {
        const walletHistory = walletHistorySnapshot.val();
        
        // Find and DELETE the Processing transaction
        for (const [key, transaction] of Object.entries(walletHistory)) {
          if (transaction.transaction_id === entry.id && transaction.type === 'Processing') {
            // Delete the Processing entry using remove()
            await remove(child(walletHistoryRef(entry.uid), key));
            break;
          }
        }
      }
      
      // Add a new credit entry to wallet history for the cancelled amount
      const creditEntry = {
        type: 'Credit',
        amount: parseFloat(entry.amount),
        date: new Date().getTime(),
        txRef: 'cancelled_withdraw_' + entry.id,
        transaction_id: 'cancelled_' + entry.id,
        description: `Withdrawal cancellation refund - ${reverseReason || 'Request cancelled by admin'}`,
        note: `Refunded from cancelled withdrawal request #${entry.id}. Reason: ${reverseReason || 'Request cancelled by admin'}. Cancelled by: ${reversedBy}`
      };
      
      await push(walletHistoryRef(entry.uid), creditEntry);

      // Notify the driver with the cancellation reason
      if (driverData && driverData.pushToken) {
        const language = store.getState()?.languagedata?.defaultLanguage || {};
        RequestPushMsg(
          driverData.pushToken,
          {
            title: language.withdraw_cancelled_title || 'Withdrawal Cancelled',
            msg: (language.withdraw_cancelled_notification || 'Your withdrawal request was cancelled and the amount was returned to your wallet. Reason: ') + (reverseReason || ''),
            screen: 'Wallet',
            ios: driverData.userPlatform === "IOS"
          }
        );
      }
    } catch (error) {
      console.error('Error reversing withdrawal:', error);
      throw error;
    }
  }
}